import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import {
  errorMessages as addOriginalPackageToCartAndPlaceOrderErrorMessages,
  messageDefiner as addOriginalPackageToCartAndPlaceOrderMessageDefiner,
} from
  '../../constants/apiErrorMessages/addOriginalPackageToCartAndPlaceOrderMutationsErrorMessages';
import {
  errorMessages as addOriginalPackageToCartErrorMessages,
  messageDefiner as addOriginalPackageToCartMessageDefiner,
} from '../../constants/apiErrorMessages/addOriginalPackageToCartMutationErrorMessages';
import {
  LayoutExtensionContainer,
} from '../../containers/LayoutExtensionContainer/LayoutExtensionContainer';
import { errorHooksIntl } from '../../hooks/errorHooksIntl/errorHooksIntl';
import { ErrorPresentational } from '../../types/common';
import { Animations, Modal } from '../Modal/Modal';
import { Typography } from '../Typography/Typography';
import styles from './ErrorModal.module.css';

const messages = defineMessages({
  errorMessage: {
    id: 'src.components.ErrorModal.errorMessage',
    defaultMessage: 'Oh oh, something went wrong. We are already working on a fix for this issue. Please try again at a later moment. Our apologies for the inconvenience.',
  },
});

export const hooks = {
  useErrorMessage({ apiError }: UseErrorMessageParams): string | JSX.Element {
    const { translateErrorCallback } = errorHooksIntl.useTranslateErrorCallback({
      ids: [
        ...addOriginalPackageToCartMessageDefiner,
        ...addOriginalPackageToCartAndPlaceOrderMessageDefiner,
      ],
      messages: {
        ...addOriginalPackageToCartErrorMessages,
        ...addOriginalPackageToCartAndPlaceOrderErrorMessages,
      },
    });

    return React.useMemo((): string | JSX.Element => {
      const translatedError = translateErrorCallback(apiError);

      return translatedError?.intlMessageFormatted
        || <FormattedMessage {...messages.errorMessage} />;
    }, [apiError, translateErrorCallback]);
  },
};

export type UseErrorMessageParams = {
  apiError: ErrorPresentational | null;
};

export type Props = UseErrorMessageParams & {
  onCloseModal: () => void;
};

export const ErrorModal = React.memo((props: Props) => {
  const {
    apiError,
    onCloseModal,
  } = props;

  const errorMessage = hooks.useErrorMessage({ apiError });

  return (
    <LayoutExtensionContainer>
      {({ isMobileLayout }) => (
        <Modal
          isMobileLayout={isMobileLayout}
          isOpen={apiError !== null}
          overlayClassName={styles.errorModalOverlay}
          animation={{
            desktop: Animations.opacity,
            mobile: Animations.fromDownToTop,
          }}
          modalContentWrapperClassName={{ base: styles.modalContentWrapper }}
          modalContentClassName={styles.errorModalContent}
          onClose={onCloseModal}
        >
          <div>
            <Typography component='h4' variant='body1'>
              {errorMessage}
            </Typography>
          </div>
        </Modal>
      )}
    </LayoutExtensionContainer>
  );
});

ErrorModal.displayName = 'ErrorModal';
